import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Loader2, Trash2, Check, Mail, Phone, Car } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { ADMIN } from "@/constants/testIds";

export default function AdminQuotes() {
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/quotes");
      setQuotes(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load quotes");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const markStatus = async (id, status) => {
    try {
      await api.patch(`/admin/quotes/${id}`, { status });
      setQuotes((qs) => qs.map((q) => (q.id === id ? { ...q, status } : q)));
      toast.success(`Marked as ${status}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Update failed");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this quote request?")) return;
    try {
      await api.delete(`/admin/quotes/${id}`);
      setQuotes((qs) => qs.filter((q) => q.id !== id));
      toast.success("Quote deleted");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Delete failed");
    }
  };

  return (
    <div>
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-3">
          <span className="h-px w-8 bg-cyan-400" />
          <span className="text-cyan-400 uppercase tracking-[0.35em] text-xs font-medium">
            Inbox
          </span>
        </div>
        <h1 className="font-heading text-3xl md:text-4xl font-black uppercase tracking-tighter">
          Quote Requests
        </h1>
        <p className="text-zinc-400 mt-2">{quotes.length} total submissions</p>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-zinc-400">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading...
        </div>
      ) : quotes.length === 0 ? (
        <div className="border border-white/10 bg-white/[0.02] backdrop-blur-md p-10 text-zinc-400 text-center">
          No quote requests yet. Once customers submit the contact form, they&apos;ll appear here.
        </div>
      ) : (
        <div className="border border-white/10 bg-white/[0.02] backdrop-blur-md overflow-x-auto">
          <Table data-testid={ADMIN.quotesTable}>
            <TableHeader>
              <TableRow className="border-white/10 hover:bg-transparent">
                <TableHead className="text-cyan-400 uppercase text-xs tracking-widest">Status</TableHead>
                <TableHead className="text-cyan-400 uppercase text-xs tracking-widest">Contact</TableHead>
                <TableHead className="text-cyan-400 uppercase text-xs tracking-widest">Service</TableHead>
                <TableHead className="text-cyan-400 uppercase text-xs tracking-widest">Details</TableHead>
                <TableHead className="text-cyan-400 uppercase text-xs tracking-widest">Date</TableHead>
                <TableHead className="text-right text-cyan-400 uppercase text-xs tracking-widest">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {quotes.map((q) => (
                <TableRow
                  key={q.id}
                  data-testid={ADMIN.quoteRow(q.id)}
                  className="border-white/10 hover:bg-white/5"
                >
                  <TableCell>
                    <Badge
                      variant="outline"
                      className={`uppercase tracking-widest text-[10px] rounded-none border ${
                        q.status === "new"
                          ? "border-cyan-400 text-cyan-400 bg-cyan-400/10"
                          : q.status === "read"
                          ? "border-white/20 text-zinc-300"
                          : "border-white/10 text-zinc-500"
                      }`}
                    >
                      {q.status}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <div className="text-white font-medium">{q.name}</div>
                    <div className="text-xs text-zinc-400 flex items-center gap-1.5 mt-1">
                      <Phone className="w-3 h-3" />
                      {q.phone}
                    </div>
                    {q.email && (
                      <div className="text-xs text-zinc-400 flex items-center gap-1.5 mt-1">
                        <Mail className="w-3 h-3" />
                        {q.email}
                      </div>
                    )}
                  </TableCell>
                  <TableCell className="text-zinc-200">{q.service_type}</TableCell>
                  <TableCell className="max-w-xs">
                    {q.vehicle_or_property && (
                      <div className="text-xs text-zinc-400 flex items-start gap-1.5">
                        <Car className="w-3 h-3 mt-0.5" />
                        {q.vehicle_or_property}
                      </div>
                    )}
                    {q.message && (
                      <div className="text-xs text-zinc-300 mt-1 line-clamp-3">{q.message}</div>
                    )}
                  </TableCell>
                  <TableCell className="text-xs text-zinc-400 whitespace-nowrap">
                    {q.created_at ? new Date(q.created_at).toLocaleString() : "—"}
                  </TableCell>
                  <TableCell className="text-right whitespace-nowrap">
                    {q.status !== "read" && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => markStatus(q.id, "read")}
                        data-testid={ADMIN.quoteMarkRead(q.id)}
                        className="rounded-none border-white/10 bg-transparent hover:border-cyan-400 hover:text-cyan-400 text-zinc-300 mr-2"
                      >
                        <Check className="w-3.5 h-3.5" />
                      </Button>
                    )}
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => remove(q.id)}
                      data-testid={ADMIN.quoteDelete(q.id)}
                      className="rounded-none border-white/10 bg-transparent hover:border-red-400 hover:text-red-400 text-zinc-300"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}
